import { useState, useEffect, useCallback } from 'react'
import { apiService, ApiError } from '../services/api'
import { User, DealershipRole, UserContext } from '../types'
import { expandPermissionKeys } from '../utils/accessControl'

function getAvatarStorageKey(userId: string) {
 return `ambcar.profile.avatar.${userId}`
}

function decodeJwtPayload(token: string): { exp?: number } | null {
 const parts = token.split('.')
 if (parts.length < 2) return null
 const segment = parts[1]
 try {
 const base64 = segment.replace(/-/g, '+').replace(/_/g, '/')
 const padded = base64 + '='.repeat((4 - (base64.length % 4)) % 4)
 return JSON.parse(atob(padded)) as { exp?: number }
 } catch {
 return null
 }
}

type MeResponse = {
 id: string
 email: string
 name: string
 context: UserContext
 dealershipId?: string
 backofficeRole?: string
 dealershipRole?: string
}

function mapMeToUser(me: MeResponse, permissions: string[] = [], avatar?: string | null): User {
 const dealershipRole = (me.dealershipRole === 'seller' ? 'salesperson' : me.dealershipRole) as
 | DealershipRole
 | undefined

 return {
  id: me.id,
  email: me.email,
  name: me.name,
  context: me.context,
  backofficeRole: me.backofficeRole as User['backofficeRole'],
  dealershipId: me.dealershipId,
  dealershipRole,
  permissions,
  isActive: true,
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  phones: [],
  status: 'active',
  role: {
   id: me.id,
   name: me.dealershipRole || 'user',
   description: '',
  },
  avatar: avatar ?? undefined,
 }
}

function clearAuthStorage() {
 localStorage.removeItem('accessToken')
 localStorage.removeItem('refreshToken')
 localStorage.removeItem('user')
 localStorage.removeItem('dealership')
 localStorage.removeItem('userPermissions')
 Object.keys(localStorage).forEach((key) => {
  if (key.startsWith('ambcar.profile.avatar.')) {
   localStorage.removeItem(key)
  }
 })
}

export const useRealAuth = () => {
 const [user, setUser] = useState<User | null>(null)
 const [token, setToken] = useState<string | null>(null)
 const [isLoading, setIsLoading] = useState(true)

 const hydrateSession = useCallback(async (accessToken: string, initialPermissions?: string[]) => {
 setToken(accessToken)
 const profileRes = await apiService.getMe()
 const me = profileRes.user
 const storedAvatar = localStorage.getItem(getAvatarStorageKey(me.id))
 const userObject = mapMeToUser(
   me,
   Array.from(expandPermissionKeys(initialPermissions ?? [])),
   storedAvatar,
 )
 setUser(userObject)
 localStorage.setItem('user', JSON.stringify({
   id: me.id,
   email: me.email,
   name: me.name,
   avatar: storedAvatar,
 }))
 }, [])

 useEffect(() => {
 const bootstrap = async () => {
 const savedToken = localStorage.getItem('accessToken')
 if (!savedToken) {
 setIsLoading(false)
 return
 }
 try {
 await hydrateSession(savedToken)
 } catch (error) {
 console.error('Sessão inválida:', error)
 clearAuthStorage()
 setToken(null)
 setUser(null)
 } finally {
 setIsLoading(false)
 }
 }
 void bootstrap()
 }, [hydrateSession])

 useEffect(() => {
 const handleAutoLogout = () => {
 setToken(null)
 setUser(null)
 }
 window.addEventListener('auth:logout', handleAutoLogout as EventListener)
 return () => {
 window.removeEventListener('auth:logout', handleAutoLogout as EventListener)
 }
 }, [])

 const login = async (email: string, password: string) => {
 try {
 const response = await apiService.login({ email, password })
 const responseData = response.data || response

 if (responseData && responseData.accessToken) {
 const { accessToken, refreshToken, dealership, user: loginUser } = responseData
 const loginPermissions = Array.isArray(loginUser?.permissions) ? loginUser.permissions : []

 localStorage.setItem('accessToken', accessToken)
 localStorage.setItem('refreshToken', refreshToken)

 if (dealership != null) {
 localStorage.setItem('dealership', JSON.stringify(dealership))
 } else {
 localStorage.removeItem('dealership')
 }

 await hydrateSession(accessToken, loginPermissions)
 return Promise.resolve()
 }
 throw new Error('Resposta inválida da API')
 } catch (error) {
 if (error instanceof ApiError) {
 throw new Error(error.message)
 }
 throw new Error('Erro de conexão. Verifique sua internet e tente novamente.')
 }
 }

 const logout = () => {
 clearAuthStorage()
 setToken(null)
 setUser(null)
 }

 const isTokenValid = () => {
 if (!token) return false
 const payload = decodeJwtPayload(token)
 if (!payload || typeof payload.exp !== 'number') return false
 const currentTime = Math.floor(Date.now() / 1000)
 const timeUntilExpiry = payload.exp - currentTime
 if (timeUntilExpiry < 300 && timeUntilExpiry > 0) {
 void refreshAccessToken()
 }
 return payload.exp > currentTime
 }

 const refreshAccessToken = async () => {
 const refreshToken = localStorage.getItem('refreshToken')
 if (!refreshToken) {
 logout()
 return false
 }
 try {
 const response = await apiService.refreshToken(refreshToken)
 const responseData = response.data || response
 if (responseData && responseData.accessToken) {
 const { accessToken, refreshToken: newRefreshToken } = responseData
 localStorage.setItem('accessToken', accessToken)
 localStorage.setItem('refreshToken', newRefreshToken)
 setToken(accessToken)
 try {
 await hydrateSession(accessToken)
 } catch {
 logout()
 return false
 }
 return true
 }
 } catch (error) {
 console.error('Erro ao renovar token:', error)
 logout()
 }
 return false
 }

 const updateUser = (newUserData: Partial<User>) => {
 if (!user) return
 const { context, dealershipRole, backofficeRole, permissions, role, ...safe } = newUserData
 const updatedUser = { ...user, ...safe }
 setUser(updatedUser)
 localStorage.setItem('user', JSON.stringify({
   id: updatedUser.id,
   email: updatedUser.email,
   name: updatedUser.name,
   avatar: updatedUser.avatar ?? null,
 }))
 if (updatedUser.avatar) {
   localStorage.setItem(getAvatarStorageKey(updatedUser.id), updatedUser.avatar)
 } else {
   localStorage.removeItem(getAvatarStorageKey(updatedUser.id))
 }
 }

 return {
 user,
 token,
 login,
 logout,
 isLoading,
 isAuthenticated: !!token && !!user && isTokenValid(),
 updateUser,
 isTokenValid,
 refreshAccessToken,
 }
}
